import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AdminPagination {
  page: number;
  lastPage: number;
  total: number;
  perPage: number;
}

export interface AdminUIState {
  pendingRequests: number;
  loading: boolean;
  error: string | null;
  stats: Record<string, any>;
  filters: Record<string, any>;
  selectedUsers: any[];
  selectedTenants: any[];
  selectedRoles: any[];
  users: any[];
  roles: any[];
  tenants: any[];
  plans: any[];
  backups: any[];
  logs: any[];
  settings: Record<string, any>;
  pagination: AdminPagination;
  dashboardStats: any | null;
  systemHealth: any | null;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  availableUsers: any[];
  permissions: any[];
  totalPlans: number;
  activePlans: number;
  freePlans: number;
  hasBackupInProgress: boolean;
}

const initialState: AdminUIState = {
  pendingRequests: 0,
  loading: false,
  error: null,
  stats: {},
  filters: {},
  selectedUsers: [],
  selectedTenants: [],
  selectedRoles: [],
  users: [],
  roles: [],
  tenants: [],
  plans: [],
  backups: [],
  logs: [],
  settings: {},
  pagination: { page: 1, lastPage: 1, total: 0, perPage: 10 },
  dashboardStats: null,
  systemHealth: null,
  sortField: '',
  sortDirection: 'asc',
  availableUsers: [],
  permissions: [],
  totalPlans: 0,
  activePlans: 0,
  freePlans: 0,
  hasBackupInProgress: false,
};

const adminUISlice = createSlice({
  name: 'adminUI',
  initialState,
  reducers: {
    adminRequestStarted(state) {
      state.pendingRequests += 1;
      state.loading = true;
      state.error = null;
    },
    adminRequestFinished(state) {
      state.pendingRequests = Math.max(0, state.pendingRequests - 1);
      state.loading = state.pendingRequests > 0;
    },
    updateAdminData(state, action: PayloadAction<Partial<AdminUIState>>) {
      Object.assign(state, action.payload);
    },
    setAdminLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setAdminError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  setAdminFilters(state, action: PayloadAction<Record<string, any>>) {
    state.filters = action.payload;
  },
  setAdminSort(state, action: PayloadAction<{ field: string; direction: 'asc' | 'desc' }>) {
    state.sortField = action.payload.field;
    state.sortDirection = action.payload.direction;
  },
  setAdminPagination(state, action: PayloadAction<Partial<AdminPagination>>) {
    // Only the known keys are accepted and each is coerced to a non-negative
    // integer: a spread of an arbitrary partial used to write NaN/negative
    // pages into state, leaving the grid on a page that does not exist.
    const next = action.payload ?? {};
    const count = (value: unknown, fallback: number): number => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : fallback;
    };
    state.pagination = {
      page: Math.max(1, count(next.page, state.pagination.page)),
      lastPage: Math.max(1, count(next.lastPage, state.pagination.lastPage)),
      total: count(next.total, state.pagination.total),
      perPage: Math.max(1, count(next.perPage, state.pagination.perPage)),
    };
  },
  setSelectedUsers(state, action: PayloadAction<any[]>) {
    // Copy: storing the caller's array reference lets a later in-place mutation
    // (push/splice on the selection) bypass Redux entirely — no re-render, stale
    // selection.
    state.selectedUsers = [...action.payload];
  },
  setSelectedTenants(state, action: PayloadAction<any[]>) {
    state.selectedTenants = [...action.payload];
  },
  setSelectedRoles(state, action: PayloadAction<any[]>) {
    state.selectedRoles = [...action.payload];
  },
  setAdminStats(state, action: PayloadAction<Record<string, any>>) {
    state.stats = { ...action.payload };
  },
    setPlansSummary(state, action: PayloadAction<{ total: number; active: number; free: number }>) {
      state.totalPlans = action.payload.total;
      state.activePlans = action.payload.active;
      state.freePlans = action.payload.free;
    },
    setBackupInProgress(state, action: PayloadAction<boolean>) {
      state.hasBackupInProgress = action.payload;
    },
    resetAdminUI() {
      return initialState;
    },
  },
});

export const {
  adminRequestStarted,
  adminRequestFinished,
  updateAdminData,
  setAdminLoading,
  setAdminError,
  setAdminFilters,
  setAdminSort,
  setAdminPagination,
  setSelectedUsers,
  setSelectedTenants,
  setSelectedRoles,
  setAdminStats,
  setPlansSummary,
  setBackupInProgress,
  resetAdminUI,
} = adminUISlice.actions;

export default adminUISlice.reducer;
